'use client';

import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from '@/config';
import type { DrilldownTableFilters } from './DrilldownTable';

// ============================================================================
// Styled Components
// ============================================================================

const TimeWindowContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const TimeWindowButton = styled.button<{ $active: boolean }>`
  color: '#21252E';
  font-size: ${theme.fontSizes.sm};
  font-weight: ${({ $active }) => ($active ? theme.fontWeights.semibold : theme.fontWeights.normal)};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  background: none;
  border: none;
  padding: 0;

  ${({ $active }) =>
    $active &&
    css`
      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: 2px;
        background: ${theme.colors.gradients.primary};
      }
    `}

  &:hover {
    color: ${theme.colors.primary.accent};
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface TimeWindowOption {
  value: string;
  label: string;
}

interface TimeWindowButtonsProps {
  filters: DrilldownTableFilters;
  onFilterApply: (filters: DrilldownTableFilters) => void;
  timeWindowOptions?: TimeWindowOption[];
}

// ============================================================================
// Component
// ============================================================================

const defaultTimeWindowOptions: TimeWindowOption[] = [
  { value: '24h', label: '1D' },
  { value: '7d', label: '7D' },
  { value: '15d', label: '15D' },
  { value: '30d', label: '30D' },
];

export const TimeWindowButtons: React.FC<TimeWindowButtonsProps> = ({ 
  filters, 
  onFilterApply,
  timeWindowOptions = defaultTimeWindowOptions 
}) => {
  const handleTimeWindowClick = (window: string) => {
    const newFilters = { ...filters, window, start_date: '', end_date: '' };
    onFilterApply(newFilters);
  };

  return (
    <TimeWindowContainer>
      {timeWindowOptions.map((option) => (
        <TimeWindowButton
          key={option.value}
          $active={filters.window === option.value}
          onClick={() => handleTimeWindowClick(option.value)}
          type="button"
        >
          {option.label}
        </TimeWindowButton>
      ))}
    </TimeWindowContainer>
  );
};

// Export as QuickLinks alias
export const QuickLinks = TimeWindowButtons;

